import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch } from '../../lib/api';

interface Condizione {
  id: number;
  bancaId: number;
  dataDecorrenza: string;
  tassoDebitore: number | null;
  tassoCreditore: number | null;
  speseTenutaConto: number | null;
  commissioneDF: number | null;
  periodicitaCC: string | null;
  fidoImporto: number | null;
  fidoTasso: number | null;
  fidoScadenza: string | null;
  anticipoPercentuale: number | null;
  anticipoTasso: number | null;
  anticipoCommissioni: number | null;
  note: string | null;
  createdAt: string;
  inseritoDa: number;
  utenteNome: string | null;
}

interface Banca {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  nome: string;
  abi: string | null;
  cab: string | null;
  iban: string | null;
  attiva: boolean;
  createdAt: string;
  updatedAt: string;
  condizioni: Condizione[];
}

// ── Helpers ───────────────────────────────────────────────────────────────
function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}
function fmtDateTime(d: string): string {
  return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtEuro(n: number): string {
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(n: number): string {
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}
function formatIBANDisplay(iban: string): string {
  const cleaned = iban.replace(/\s/g, '');
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}
function maskIBAN(iban: string): string {
  const cleaned = iban.replace(/\s/g, '');
  if (cleaned.length <= 4) return cleaned;
  const visible = cleaned.slice(-4);
  const masked = '•'.repeat(cleaned.length - 4) + visible;
  return masked.match(/.{1,4}/g)?.join(' ') ?? masked;
}
function validaIBAN(iban: string): boolean {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  return /^IT\d{2}[A-Z0-9]{23}$/.test(cleaned);
}
function formatIBANInput(val: string): string {
  const cleaned = val.replace(/\s/g, '').toUpperCase();
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

// ── Algoritmo generazione IBAN italiano ──────────────────────────────────
const CIN_DISPARI: Record<string, number> = {
  '0': 1,  '1': 0,  '2': 5,  '3': 7,  '4': 9,  '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1,  'B': 0,  'C': 5,  'D': 7,  'E': 9,  'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2,  'L': 4,  'M': 18, 'N': 20, 'O': 11, 'P': 3,  'Q': 6,  'R': 8,  'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23,
};
const CIN_PARI: Record<string, number> = {
  '0': 0,  '1': 1,  '2': 2,  '3': 3,  '4': 4,  '5': 5,  '6': 6,  '7': 7,  '8': 8,  '9': 9,
  'A': 0,  'B': 1,  'C': 2,  'D': 3,  'E': 4,  'F': 5,  'G': 6,  'H': 7,  'I': 8,  'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
  'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25,
};

function calcolaCIN(abi: string, cab: string, conto: string): string {
  const s = abi.padStart(5, '0') + cab.padStart(5, '0') + conto.padStart(12, '0').toUpperCase();
  let sum = 0;
  for (let i = 0; i < s.length; i++) {
    sum += ((i + 1) % 2 === 1 ? CIN_DISPARI : CIN_PARI)[s[i]] ?? 0;
  }
  return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[sum % 26];
}

function generaIBAN(abi: string, cab: string, conto: string): string {
  const a = abi.padStart(5, '0');
  const c = cab.padStart(5, '0');
  const n = conto.padStart(12, '0').toUpperCase();
  const cin = calcolaCIN(a, c, n);
  const bban = cin + a + c + n;
  const numeric = (bban + 'IT00').split('').map(ch => {
    const code = ch.charCodeAt(0);
    return code >= 65 && code <= 90 ? (code - 55).toString() : ch;
  }).join('');
  let rem = 0;
  for (const d of numeric) rem = (rem * 10 + parseInt(d, 10)) % 97;
  const check = (98 - rem).toString().padStart(2, '0');
  return 'IT' + check + bban;
}

function estraiConto(iban: string): string {
  const raw = iban.replace(/\s/g, '').toUpperCase();
  if (raw.length !== 27 || !raw.startsWith('IT')) return '';
  return raw.slice(15);
}

// ── Scadenza fido: colore badge ───────────────────────────────────────────
function fidoScadenzaInfo(scadenza: string | null): { label: string; badgeClass: string } | null {
  if (!scadenza) return null;
  const diff = new Date(scadenza).getTime() - Date.now();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: 'Scaduto', badgeClass: 'bop-badge bop-badge--danger' };
  if (days <= 90) return { label: `Scade tra ${days}g`, badgeClass: 'bop-badge bop-badge--warning' };
  return null;
}

// ── Sezione condizioni correnti: mostra solo campi non nulli ──────────────
function rigaCC(c: Condizione): React.ReactNode[] {
  const rows: React.ReactNode[] = [];
  if (c.tassoDebitore != null) rows.push(<RigaValore key="td" label="Tasso debitore" valore={fmtPct(c.tassoDebitore)} />);
  if (c.tassoCreditore != null) rows.push(<RigaValore key="tc" label="Tasso creditore" valore={fmtPct(c.tassoCreditore)} />);
  if (c.speseTenutaConto != null) rows.push(
    <RigaValore key="stc" label="Spese tenuta conto" valore={`€ ${fmtEuro(c.speseTenutaConto)}${c.periodicitaCC ? ` (${c.periodicitaCC})` : ''}`} />
  );
  if (c.commissioneDF != null) rows.push(<RigaValore key="cdf" label="Commissione disp. fondi" valore={fmtPct(c.commissioneDF)} />);
  return rows;
}
function rigaFido(c: Condizione): React.ReactNode[] {
  const rows: React.ReactNode[] = [];
  if (c.fidoImporto != null) rows.push(<RigaValore key="fi" label="Importo accordato" valore={`€ ${fmtEuro(c.fidoImporto)}`} />);
  if (c.fidoTasso != null) rows.push(<RigaValore key="ft" label="Tasso" valore={fmtPct(c.fidoTasso)} />);
  if (c.fidoScadenza != null) {
    const info = fidoScadenzaInfo(c.fidoScadenza);
    rows.push(
      <div key="fs" className="bop-banca__fido-scadenza-row">
        <span className="bop-banca__riga-label">Scadenza</span>
        <div className="bop-banca__fido-scadenza-right">
          <span className="bop-banca__riga-value">{fmtDate(c.fidoScadenza)}</span>
          {info && <span className={info.badgeClass}>{info.label}</span>}
        </div>
      </div>
    );
  }
  return rows;
}
function rigaAnticipo(c: Condizione): React.ReactNode[] {
  const rows: React.ReactNode[] = [];
  if (c.anticipoPercentuale != null) rows.push(<RigaValore key="ap" label="% anticipabile" valore={fmtPct(c.anticipoPercentuale)} />);
  if (c.anticipoTasso != null) rows.push(<RigaValore key="at" label="Tasso anticipo" valore={fmtPct(c.anticipoTasso)} />);
  if (c.anticipoCommissioni != null) rows.push(<RigaValore key="ac" label="Commissioni" valore={fmtPct(c.anticipoCommissioni)} />);
  return rows;
}

function RigaValore({ label, valore }: { label: string; valore: string }) {
  return (
    <div className="bop-banca__riga-valore">
      <span className="bop-banca__riga-label">{label}</span>
      <span className="bop-banca__riga-value">{valore}</span>
    </div>
  );
}

// ── Sezione condizioni correnti ───────────────────────────────────────────
function SezioneCond({ title, icon, rows }: { title: string; icon: string; rows: React.ReactNode[] }) {
  return (
    <div className="bop-banca__sezione">
      <div className="bop-banca__sezione-title">
        {icon} {title}
      </div>
      {rows.length === 0 ? (
        <span className="bop-banca__sezione-empty">Non configurato</span>
      ) : rows}
    </div>
  );
}

// ── Accordion ─────────────────────────────────────────────────────────────
function Accordion({ title, expanded, onToggle, children }: {
  title: string; expanded: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="bop-banca__accordion">
      <button
        type="button"
        onClick={onToggle}
        className={`bop-banca__accordion-trigger ${expanded ? 'bop-banca__accordion-trigger--expanded' : 'bop-banca__accordion-trigger--collapsed'}`}
      >
        {title}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          className={`bop-banca__accordion-icon ${expanded ? 'bop-banca__accordion-icon--expanded' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded && <div className="bop-banca__accordion-body">{children}</div>}
    </div>
  );
}

const PERIODICITA = ['Mensile', 'Trimestrale', 'Semestrale', 'Annuale'];

// ── Componente principale ─────────────────────────────────────────────────
export default function DettaglioBanca() {
  const { id } = useParams<{ id: string }>();

  const [banca, setBanca] = useState<Banca | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ibanVisible, setIbanVisible] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // ── Modal banca modifica ──────────────────────────────────────────────────
  const [showEditBanca, setShowEditBanca] = useState(false);
  const [formNome, setFormNome] = useState('');
  const [formAbi, setFormAbi] = useState('');
  const [formCab, setFormCab] = useState('');
  const [formConto, setFormConto] = useState('');
  const [formIban, setFormIban] = useState('');
  const [formIbanAutoGen, setFormIbanAutoGen] = useState(false);
  const [formIbanError, setFormIbanError] = useState('');
  const [isSavingBanca, setIsSavingBanca] = useState(false);

  // ── Modal condizione ──────────────────────────────────────────────────────
  const [showCondModal, setShowCondModal] = useState(false);
  const [editCond, setEditCond] = useState<Condizione | null>(null);
  const [condForm, setCondForm] = useState({
    dataDecorrenza: '',
    tassoDebitore: '', tassoCreditore: '', speseTenutaConto: '', commissioneDF: '', periodicitaCC: '',
    fidoImporto: '', fidoTasso: '', fidoScadenza: '',
    anticipoPercentuale: '', anticipoTasso: '', anticipoCommissioni: '',
    note: '',
  });
  const [accExpanded, setAccExpanded] = useState({ cc: true, fido: false, anticipo: false });
  const [isSavingCond, setIsSavingCond] = useState(false);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Fetch banca ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    (async () => {
      setIsLoading(true);
      try {
        const data = await apiFetch<Banca>(`/api/banche/${id}`);
        setBanca(data);
      } catch (err: any) {
        showToast(err.message || 'Errore caricamento', 'error');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  // ── Auto-genera IBAN quando ABI + CAB + conto sono presenti ─────────────
  useEffect(() => {
    if (!showEditBanca) return;
    const a = formAbi.trim();
    const c = formCab.trim();
    const n = formConto.trim().replace(/\s/g, '');
    if (a.length === 5 && c.length === 5 && n.length > 0) {
      const iban = generaIBAN(a, c, n);
      setFormIban(formatIBANDisplay(iban));
      setFormIbanError('');
      setFormIbanAutoGen(true);
    } else if (formIbanAutoGen) {
      setFormIban('');
      setFormIbanAutoGen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formAbi, formCab, formConto, showEditBanca]);

  // ── Apri modifica dati banca ──────────────────────────────────────────────
  function openEditBanca() {
    if (!banca) return;
    setFormNome(banca.nome);
    setFormAbi(banca.abi ?? '');
    setFormCab(banca.cab ?? '');
    const conto = banca.iban ? estraiConto(banca.iban) : '';
    setFormConto(conto);
    setFormIban(banca.iban ? formatIBANDisplay(banca.iban) : '');
    setFormIbanError('');
    setFormIbanAutoGen(false);
    setShowEditBanca(true);
  }

  function handleIbanChange(val: string) {
    const formatted = formatIBANInput(val);
    setFormIban(formatted);
    setFormIbanAutoGen(false);
    const raw = formatted.replace(/\s/g, '');
    if (raw && !validaIBAN(raw)) setFormIbanError('Formato non valido');
    else setFormIbanError('');
  }

  async function handleSalvaBanca() {
    if (!banca || !formNome.trim()) return showToast('Il nome è obbligatorio', 'error');
    const rawIban = formIban.replace(/\s/g, '');
    if (rawIban && !validaIBAN(rawIban)) return showToast('IBAN non valido', 'error');
    setIsSavingBanca(true);
    try {
      const updated = await apiFetch<Banca>(`/api/banche/${banca.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nome: formNome.trim(),
          abi: formAbi.trim() || null,
          cab: formCab.trim() || null,
          iban: rawIban || null,
        }),
      });
      setBanca(prev => prev ? { ...prev, ...updated, condizioni: prev.condizioni } : prev);
      showToast('Dati aggiornati');
      setShowEditBanca(false);
    } catch (err: any) {
      showToast(err.message || 'Errore salvataggio', 'error');
    } finally {
      setIsSavingBanca(false);
    }
  }

  async function handleDisattiva() {
    if (!banca || !window.confirm(`Disattivare la banca "${banca.nome}"?`)) return;
    try {
      await apiFetch(`/api/banche/${banca.id}`, { method: 'DELETE' });
      setBanca(prev => prev ? { ...prev, attiva: false } : prev);
      showToast('Banca disattivata');
    } catch (err: any) {
      showToast(err.message || 'Errore disattivazione', 'error');
    }
  }

  // ── Apri modal condizione ─────────────────────────────────────────────────
  function openNuovaCondizione() {
    setEditCond(null);
    setCondForm({
      dataDecorrenza: new Date().toISOString().slice(0, 10),
      tassoDebitore: '', tassoCreditore: '', speseTenutaConto: '', commissioneDF: '', periodicitaCC: '',
      fidoImporto: '', fidoTasso: '', fidoScadenza: '',
      anticipoPercentuale: '', anticipoTasso: '', anticipoCommissioni: '',
      note: '',
    });
    setAccExpanded({ cc: true, fido: false, anticipo: false });
    setShowCondModal(true);
  }

  function openEditCondizione(c: Condizione) {
    setEditCond(c);
    setCondForm({
      dataDecorrenza: c.dataDecorrenza.slice(0, 10),
      tassoDebitore: c.tassoDebitore != null ? String(c.tassoDebitore) : '',
      tassoCreditore: c.tassoCreditore != null ? String(c.tassoCreditore) : '',
      speseTenutaConto: c.speseTenutaConto != null ? String(c.speseTenutaConto) : '',
      commissioneDF: c.commissioneDF != null ? String(c.commissioneDF) : '',
      periodicitaCC: c.periodicitaCC ?? '',
      fidoImporto: c.fidoImporto != null ? String(c.fidoImporto) : '',
      fidoTasso: c.fidoTasso != null ? String(c.fidoTasso) : '',
      fidoScadenza: c.fidoScadenza ? c.fidoScadenza.slice(0, 10) : '',
      anticipoPercentuale: c.anticipoPercentuale != null ? String(c.anticipoPercentuale) : '',
      anticipoTasso: c.anticipoTasso != null ? String(c.anticipoTasso) : '',
      anticipoCommissioni: c.anticipoCommissioni != null ? String(c.anticipoCommissioni) : '',
      note: c.note ?? '',
    });
    setAccExpanded({ cc: true, fido: false, anticipo: false });
    setShowCondModal(true);
  }

  function parseOpt(v: string): number | null {
    if (!v.trim()) return null;
    const n = parseFloat(v.replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  async function handleSalvaCondizione() {
    if (!banca) return;
    if (!condForm.dataDecorrenza) return showToast('Data decorrenza obbligatoria', 'error');
    setIsSavingCond(true);
    try {
      const body = {
        dataDecorrenza: condForm.dataDecorrenza,
        tassoDebitore: parseOpt(condForm.tassoDebitore),
        tassoCreditore: parseOpt(condForm.tassoCreditore),
        speseTenutaConto: parseOpt(condForm.speseTenutaConto),
        commissioneDF: parseOpt(condForm.commissioneDF),
        periodicitaCC: condForm.periodicitaCC || null,
        fidoImporto: parseOpt(condForm.fidoImporto),
        fidoTasso: parseOpt(condForm.fidoTasso),
        fidoScadenza: condForm.fidoScadenza || null,
        anticipoPercentuale: parseOpt(condForm.anticipoPercentuale),
        anticipoTasso: parseOpt(condForm.anticipoTasso),
        anticipoCommissioni: parseOpt(condForm.anticipoCommissioni),
        note: condForm.note || null,
      };

      if (editCond) {
        const updated = await apiFetch<Condizione>(`/api/banche/${banca.id}/condizioni/${editCond.id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
        setBanca(prev => prev ? {
          ...prev,
          condizioni: prev.condizioni.map(c => c.id === updated.id ? updated : c)
            .sort((a, b) => new Date(b.dataDecorrenza).getTime() - new Date(a.dataDecorrenza).getTime()),
        } : prev);
        showToast('Condizione aggiornata');
      } else {
        const created = await apiFetch<Condizione>(`/api/banche/${banca.id}/condizioni`, {
          method: 'POST', body: JSON.stringify(body),
        });
        setBanca(prev => prev ? {
          ...prev,
          condizioni: [created, ...prev.condizioni]
            .sort((a, b) => new Date(b.dataDecorrenza).getTime() - new Date(a.dataDecorrenza).getTime()),
        } : prev);
        showToast('Condizione aggiunta');
      }
      setShowCondModal(false);
    } catch (err: any) {
      showToast(err.message || 'Errore salvataggio condizione', 'error');
    } finally {
      setIsSavingCond(false);
    }
  }

  async function handleEliminaCond(c: Condizione) {
    if (!banca) return;
    if (!window.confirm('Eliminare questa condizione?')) return;
    try {
      await apiFetch(`/api/banche/${banca.id}/condizioni/${c.id}`, { method: 'DELETE' });
      setBanca(prev => prev ? { ...prev, condizioni: prev.condizioni.filter(x => x.id !== c.id) } : prev);
      showToast('Condizione eliminata');
    } catch (err: any) {
      showToast(err.message || 'Errore eliminazione', 'error');
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Layout>
        <div className="bop-banca__loading">Caricamento...</div>
      </Layout>
    );
  }
  if (!banca) {
    return (
      <Layout>
        <div className="bop-banca__loading">Banca non trovata.</div>
      </Layout>
    );
  }

  const ultimaCond = banca.condizioni[0] ?? null;

  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div className={`bop-banca__toast ${toast.type === 'success' ? 'bop-banca__toast--success' : 'bop-banca__toast--error'}`}>
          {toast.msg}
        </div>
      )}

      {/* Back */}
      <BopBackLink to="/amministrazione/banche" label="Torna alle banche aziendali" />

      {/* Header */}
      <div className="page-header">
        <div className="bop-banca__header-left">
          <div>
            <h2 className="bop-banca__title">
              {banca.nome}
              <span className={`bop-banca__status-badge ${banca.attiva ? 'bop-banca__status-badge--attiva' : 'bop-banca__status-badge--disattiva'}`}>
                {banca.attiva ? 'Attiva' : 'Disattiva'}
              </span>
            </h2>
            <p className="bop-banca__subtitle">
              {[banca.abi && `ABI: ${banca.abi}`, banca.cab && `CAB: ${banca.cab}`].filter(Boolean).join(' • ')}
              {banca.iban && (
                <>
                  {(banca.abi || banca.cab) && ' • '}
                  IBAN: <span className="bop-banca__iban-mono">
                    {ibanVisible ? formatIBANDisplay(banca.iban) : maskIBAN(banca.iban)}
                  </span>
                  <button
                    onClick={() => setIbanVisible(v => !v)}
                    className="bop-banca__iban-toggle"
                    title={ibanVisible ? 'Nascondi' : 'Mostra'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {ibanVisible
                        ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>
                        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                      }
                    </svg>
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="bop-banca__header-actions">
          <button className="bop-btn-secondary" onClick={openEditBanca}>Modifica Dati</button>
          {banca.attiva && (
            <button className="bop-btn-danger bop-btn-sm" onClick={handleDisattiva}>Disattiva Banca</button>
          )}
        </div>
      </div>

      {/* Condizioni attuali */}
      <div className="bop-banca__card">
        <div className="bop-banca__card-header">
          <h3 className="bop-banca__card-title">
            Condizioni Attuali
            {ultimaCond && (
              <span className="bop-banca__card-title-date">
                dal {fmtDate(ultimaCond.dataDecorrenza)}
              </span>
            )}
          </h3>
          <button className="bop-btn-primary" onClick={openNuovaCondizione}>+ Aggiorna Condizioni</button>
        </div>

        {!ultimaCond ? (
          <div className="bop-banca__empty-cond">
            Nessuna condizione inserita. Clicca "+ Aggiorna Condizioni" per aggiungerne una.
          </div>
        ) : (
          <>
            <div className={`bop-banca__cond-sections ${ultimaCond.note ? 'bop-banca__cond-sections--with-notes' : ''}`}>
              <SezioneCond title="Conto Corrente" icon="🏦" rows={rigaCC(ultimaCond)} />
              <SezioneCond title="Fido" icon="💳" rows={rigaFido(ultimaCond)} />
              <SezioneCond title="Anticipo Fatture" icon="📄" rows={rigaAnticipo(ultimaCond)} />
            </div>
            {ultimaCond.note && (
              <div className="bop-banca__note-box">
                <span className="bop-banca__note-label">NOTE</span>
                <span className="bop-banca__note-text">{ultimaCond.note}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Storico condizioni */}
      <div className="bop-banca__card">
        <h3 className="bop-banca__storico-title">
          Storico Condizioni
        </h3>

        {banca.condizioni.length === 0 ? (
          <p className="bop-banca__storico-empty">Nessuna condizione nello storico.</p>
        ) : (
          <div className="bop-banca__timeline">
            {/* Linea verticale timeline */}
            <div className="bop-banca__timeline-line" />

            {banca.condizioni.map((c, idx) => {
              const isAttuale = idx === 0;
              const isUnica = banca.condizioni.length === 1;

              // Compatta: elenca solo campi non nulli
              const campi: string[] = [];
              if (c.tassoDebitore != null) campi.push(`Tasso deb. ${fmtPct(c.tassoDebitore)}`);
              if (c.tassoCreditore != null) campi.push(`Tasso cred. ${fmtPct(c.tassoCreditore)}`);
              if (c.speseTenutaConto != null) campi.push(`Spese c/c €${fmtEuro(c.speseTenutaConto)}`);
              if (c.commissioneDF != null) campi.push(`CDF ${fmtPct(c.commissioneDF)}`);
              if (c.fidoImporto != null) campi.push(`Fido €${fmtEuro(c.fidoImporto)}`);
              if (c.fidoTasso != null) campi.push(`Tasso fido ${fmtPct(c.fidoTasso)}`);
              if (c.anticipoPercentuale != null) campi.push(`Anticipo ${fmtPct(c.anticipoPercentuale)}`);
              if (c.anticipoTasso != null) campi.push(`Tasso ant. ${fmtPct(c.anticipoTasso)}`);

              return (
                <div key={c.id} className="bop-banca__timeline-item">
                  {/* Dot */}
                  <div className="bop-banca__timeline-dot-wrap">
                    <div className={`bop-banca__timeline-dot ${isAttuale ? 'bop-banca__timeline-dot--attuale' : 'bop-banca__timeline-dot--passato'}`} />
                  </div>

                  {/* Contenuto */}
                  <div className={`bop-banca__timeline-content ${isAttuale ? 'bop-banca__timeline-content--attuale' : 'bop-banca__timeline-content--passato'}`}>
                    <div className="bop-banca__timeline-header">
                      <div className="bop-banca__timeline-date-group">
                        <span className="bop-banca__timeline-date">
                          Dal {fmtDate(c.dataDecorrenza)}
                        </span>
                        {isAttuale && (
                          <span className="bop-banca__timeline-badge-attuale">ATTUALE</span>
                        )}
                      </div>
                      <div className="bop-banca__timeline-actions">
                        <button
                          className="bop-btn-secondary bop-btn-sm"
                          onClick={() => openEditCondizione(c)}
                        >Modifica</button>
                        <button
                          className="bop-btn-danger bop-btn-sm"
                          onClick={() => !isUnica && handleEliminaCond(c)}
                          disabled={isUnica}
                          title={isUnica ? 'Non puoi eliminare l\'unica condizione presente' : 'Elimina condizione'}
                        >Elimina</button>
                      </div>
                    </div>

                    <div className={`bop-banca__timeline-meta ${campi.length > 0 ? 'bop-banca__timeline-meta--with-campi' : ''}`}>
                      Inserito da {c.utenteNome ?? '—'} il {fmtDateTime(c.createdAt)}
                    </div>

                    {campi.length > 0 ? (
                      <div className="bop-banca__timeline-campi">
                        {campi.map((campo, i) => (
                          <span key={i} className="bop-banca__timeline-campo-tag">{campo}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="bop-banca__timeline-no-values">Nessun valore configurato</span>
                    )}

                    {c.note && (
                      <div className="bop-banca__timeline-note">
                        Note: {c.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modal Modifica Dati Banca ── */}
      {showEditBanca && (
        <div className="bop-banca__modal-overlay">
          <div className="bop-banca__modal">
            <h3 className="bop-banca__modal-title">Modifica Dati Banca</h3>
            <div className="bop-banca__modal-fields">
              <div className="bop-form-group">
                <label className="bop-label">Nome Banca *</label>
                <input type="text" value={formNome} onChange={e => setFormNome(e.target.value)} className="bop-input" />
              </div>
              <div className="bop-banca__modal-grid-2">
                <div className="bop-form-group">
                  <label className="bop-label">ABI</label>
                  <input type="text" value={formAbi} onChange={e => setFormAbi(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} className="bop-input" />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">CAB</label>
                  <input type="text" value={formCab} onChange={e => setFormCab(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} className="bop-input" />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Numero conto corrente</label>
                <input
                  type="text"
                  value={formConto}
                  onChange={e => setFormConto(e.target.value.replace(/\s/g, '').slice(0, 12).toUpperCase())}
                  placeholder="es. 000001234560"
                  maxLength={12}
                  className="bop-input bop-banca__input-mono"
                />
                <span className="bop-banca__hint">
                  Inserisci ABI, CAB e conto per generare l'IBAN automaticamente.
                </span>
              </div>
              <div className="bop-form-group">
                <label className="bop-label bop-banca__label-with-badge">
                  IBAN
                  {formIbanAutoGen && (
                    <span className="bop-banca__autogen-badge">
                      generato automaticamente
                    </span>
                  )}
                </label>
                <input type="text" value={formIban} onChange={e => handleIbanChange(e.target.value)}
                  className={`bop-input bop-banca__input-mono ${formIbanError ? 'bop-banca__iban-input--error' : ''} ${formIbanAutoGen ? 'bop-banca__iban-input--autogen' : ''}`}
                />
                {formIbanError && <span className="bop-banca__iban-error">{formIbanError}</span>}
              </div>
            </div>
            <div className="bop-banca__modal-footer">
              <button className="bop-btn-secondary" onClick={() => setShowEditBanca(false)}>Annulla</button>
              <button className={`bop-btn-primary ${isSavingBanca ? 'bop-banca__btn-saving' : ''}`} onClick={handleSalvaBanca} disabled={isSavingBanca}>
                {isSavingBanca ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Nuova / Modifica Condizione ── */}
      {showCondModal && (
        <div className="bop-banca__modal-overlay bop-banca__modal-overlay--scroll">
          <div className="bop-banca__modal bop-banca__modal--lg">
            <h3 className="bop-banca__modal-title">
              {editCond ? 'Modifica Condizione' : 'Nuova Condizione'}
            </h3>

            {/* Data decorrenza */}
            <div className="bop-banca__cond-date-field">
              <label className="bop-label">Data decorrenza *</label>
              <input type="date" value={condForm.dataDecorrenza}
                onChange={e => setCondForm(f => ({ ...f, dataDecorrenza: e.target.value }))}
                className="bop-input bop-banca__cond-date-input" />
            </div>

            <div className="bop-banca__accordions-col">
              {/* Conto Corrente */}
              <Accordion
                title="🏦 Conto Corrente"
                expanded={accExpanded.cc}
                onToggle={() => setAccExpanded(a => ({ ...a, cc: !a.cc }))}
              >
                <div className="bop-banca__modal-grid-2">
                  <div className="bop-form-group">
                    <label className="bop-label">Tasso debitore (%)</label>
                    <input type="number" step="0.01" value={condForm.tassoDebitore}
                      onChange={e => setCondForm(f => ({ ...f, tassoDebitore: e.target.value }))}
                      className="bop-input" placeholder="es. 5.50" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Tasso creditore (%)</label>
                    <input type="number" step="0.01" value={condForm.tassoCreditore}
                      onChange={e => setCondForm(f => ({ ...f, tassoCreditore: e.target.value }))}
                      className="bop-input" placeholder="es. 0.10" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Spese tenuta conto (€)</label>
                    <input type="number" step="0.01" value={condForm.speseTenutaConto}
                      onChange={e => setCondForm(f => ({ ...f, speseTenutaConto: e.target.value }))}
                      className="bop-input" placeholder="es. 15.00" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Comm. disponibilità fondi (%)</label>
                    <input type="number" step="0.01" value={condForm.commissioneDF}
                      onChange={e => setCondForm(f => ({ ...f, commissioneDF: e.target.value }))}
                      className="bop-input" placeholder="es. 0.50" />
                  </div>
                  <div className="bop-form-group bop-banca__field-full">
                    <label className="bop-label">Periodicità</label>
                    <select value={condForm.periodicitaCC}
                      onChange={e => setCondForm(f => ({ ...f, periodicitaCC: e.target.value }))}
                      className="bop-select">
                      <option value="">— nessuna —</option>
                      {PERIODICITA.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </Accordion>

              {/* Fido */}
              <Accordion
                title="💳 Fido"
                expanded={accExpanded.fido}
                onToggle={() => setAccExpanded(a => ({ ...a, fido: !a.fido }))}
              >
                <div className="bop-banca__modal-grid-2">
                  <div className="bop-form-group">
                    <label className="bop-label">Importo accordato (€)</label>
                    <input type="number" step="0.01" value={condForm.fidoImporto}
                      onChange={e => setCondForm(f => ({ ...f, fidoImporto: e.target.value }))}
                      className="bop-input" placeholder="es. 50000" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Tasso (%)</label>
                    <input type="number" step="0.01" value={condForm.fidoTasso}
                      onChange={e => setCondForm(f => ({ ...f, fidoTasso: e.target.value }))}
                      className="bop-input" placeholder="es. 4.50" />
                  </div>
                  <div className="bop-form-group bop-banca__field-full">
                    <label className="bop-label">Scadenza fido</label>
                    <input type="date" value={condForm.fidoScadenza}
                      onChange={e => setCondForm(f => ({ ...f, fidoScadenza: e.target.value }))}
                      className="bop-input bop-banca__cond-date-input" />
                  </div>
                </div>
              </Accordion>

              {/* Anticipo Fatture */}
              <Accordion
                title="📄 Anticipo Fatture"
                expanded={accExpanded.anticipo}
                onToggle={() => setAccExpanded(a => ({ ...a, anticipo: !a.anticipo }))}
              >
                <div className="bop-banca__modal-grid-3">
                  <div className="bop-form-group">
                    <label className="bop-label">% anticipabile</label>
                    <input type="number" step="0.01" value={condForm.anticipoPercentuale}
                      onChange={e => setCondForm(f => ({ ...f, anticipoPercentuale: e.target.value }))}
                      className="bop-input" placeholder="es. 80" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Tasso (%)</label>
                    <input type="number" step="0.01" value={condForm.anticipoTasso}
                      onChange={e => setCondForm(f => ({ ...f, anticipoTasso: e.target.value }))}
                      className="bop-input" placeholder="es. 3.50" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Commissioni (%)</label>
                    <input type="number" step="0.01" value={condForm.anticipoCommissioni}
                      onChange={e => setCondForm(f => ({ ...f, anticipoCommissioni: e.target.value }))}
                      className="bop-input" placeholder="es. 0.20" />
                  </div>
                </div>
              </Accordion>
            </div>

            {/* Note */}
            <div className="bop-banca__cond-note-field">
              <label className="bop-label">Note</label>
              <textarea value={condForm.note}
                onChange={e => setCondForm(f => ({ ...f, note: e.target.value }))}
                rows={3} placeholder="Note libere sulle condizioni..."
                className="bop-input bop-banca__textarea" />
            </div>

            <div className="bop-banca__modal-footer">
              <button className="bop-btn-secondary" onClick={() => setShowCondModal(false)}>Annulla</button>
              <button className={`bop-btn-primary ${isSavingCond ? 'bop-banca__btn-saving' : ''}`} onClick={handleSalvaCondizione} disabled={isSavingCond}>
                {isSavingCond ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
